/**
 * Authority Override Panel (PR #5c).
 *
 * Read-only view of a user's effective bind + discretionary limits. When
 * `canManage` is true (caller holds `admin.users.global`) the panel also
 * shows an override form with Save and Clear controls.
 *
 * Deliberately separated from UserDetail so the override UI only mounts
 * when the full UserDetail response (including authority) has loaded.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { UserAuthority, UserAuthorityOverrideRequest } from '@cortex/domain';

import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { FormField } from '@/components/ui/FormField';
import { formatMoney } from '@/components/ui/CurrencyValue';

export interface AuthorityOverridePanelProps {
  readonly authority: UserAuthority;
  readonly canManage: boolean;
  readonly isSaving: boolean;
  readonly onSetAuthority: (req: UserAuthorityOverrideRequest) => Promise<void>;
  readonly onClearAuthority: () => Promise<void>;
}

const OverrideFormSchema = z
  .object({
    bindLimitOverride: z
      .string()
      .trim()
      .refine(
        (v) => v === '' || (!isNaN(Number(v)) && Number(v) > 0),
        'Must be a positive number or blank to keep current.',
      ),
    discretionaryRangeOverride: z
      .string()
      .trim()
      .refine(
        (v) => v === '' || (!isNaN(Number(v)) && Number(v) >= 0 && Number(v) <= 100),
        'Enter a percentage between 0 and 100, or blank.',
      ),
    notes: z.string().trim().max(500),
  })
  .refine(
    (data) => {
      const anySet = data.bindLimitOverride !== '' || data.discretionaryRangeOverride !== '';
      return !anySet || data.notes.trim().length > 0;
    },
    { message: 'A reason is required when setting an override.', path: ['notes'] },
  );

type OverrideFormValues = z.infer<typeof OverrideFormSchema>;

const formatLimit = (v: string | null): string => {
  if (v === null) return '—';
  return formatMoney(Number(v));
};

const formatPct = (v: string | null): string => {
  if (v === null) return '—';
  return `${(Number(v) * 100).toFixed(1)}%`;
};

export function AuthorityOverridePanel({
  authority,
  canManage,
  isSaving,
  onSetAuthority,
  onClearAuthority,
}: AuthorityOverridePanelProps) {
  const [showForm, setShowForm] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OverrideFormValues>({
    resolver: zodResolver(OverrideFormSchema),
    defaultValues: {
      bindLimitOverride: authority.override?.bindLimitOverride
        ? String(Number(authority.override.bindLimitOverride))
        : '',
      discretionaryRangeOverride: authority.override?.discretionaryRangeOverride
        ? String(Number(authority.override.discretionaryRangeOverride) * 100)
        : '',
      notes: authority.override?.notes ?? '',
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    const req: UserAuthorityOverrideRequest = {
      bindLimitOverride: data.bindLimitOverride !== '' ? Number(data.bindLimitOverride) : null,
      discretionaryRangeOverride:
        data.discretionaryRangeOverride !== ''
          ? Number(data.discretionaryRangeOverride) / 100
          : null,
      notes: data.notes,
    };
    await onSetAuthority(req);
    setShowForm(false);
  });

  const handleClear = async () => {
    await onClearAuthority();
    reset({ bindLimitOverride: '', discretionaryRangeOverride: '', notes: '' });
    setShowForm(false);
  };

  return (
    <section className="space-y-3" aria-label="Bind authority">
      <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        Bind authority
      </h3>

      {/* Current effective values */}
      <dl className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <dt className="text-xs font-medium text-foreground">Bind limit</dt>
          <dd className="font-mono text-xs text-muted-foreground">
            {formatLimit(authority.bindLimit)}
            {authority.hasUserOverride && (
              <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-micro font-bold text-amber-700">
                override
              </span>
            )}
          </dd>
        </div>
        <div className="space-y-1">
          <dt className="text-xs font-medium text-foreground">Discretionary range</dt>
          <dd className="font-mono text-xs text-muted-foreground">
            {formatPct(authority.discretionaryRangePct)}
          </dd>
        </div>
      </dl>

      {authority.override && (
        <p className="text-xs text-muted-foreground">
          Override set{' '}
          {authority.override.updatedAt
            ? new Date(authority.override.updatedAt).toLocaleDateString('en-GB')
            : ''}
          {authority.override.notes ? ` · "${authority.override.notes}"` : ''}
        </p>
      )}

      {canManage && !showForm && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowForm(true)}
          disabled={isSaving}
        >
          {authority.hasUserOverride ? 'Edit override' : 'Set override'}
        </Button>
      )}

      {canManage && showForm && (
        <form onSubmit={onSubmit} className="space-y-3 rounded-md border border-border p-3">
          <FormField
            label="Bind limit override (R)"
            hint="Leave blank to keep the role default."
            error={errors.bindLimitOverride?.message}
          >
            <Input
              type="number"
              min={0}
              step="0.01"
              placeholder="e.g. 5000000"
              disabled={isSaving}
              {...register('bindLimitOverride')}
            />
          </FormField>

          <FormField
            label="Discretionary range override (%)"
            hint="0–100. Leave blank to keep the role default."
            error={errors.discretionaryRangeOverride?.message}
          >
            <Input
              type="number"
              min={0}
              max={100}
              step="0.1"
              placeholder="e.g. 20"
              disabled={isSaving}
              {...register('discretionaryRangeOverride')}
            />
          </FormField>

          <FormField
            label="Reason"
            required
            hint="Mandatory when setting a non-null override. Recorded in the audit log."
            error={errors.notes?.message}
          >
            <Input
              type="text"
              placeholder="Reason for this authority override…"
              disabled={isSaving}
              {...register('notes')}
            />
          </FormField>

          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save override'}
            </Button>
            {authority.hasUserOverride && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isSaving}
                onClick={handleClear}
              >
                Clear override
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isSaving}
              onClick={() => setShowForm(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
